/**
 * QuestionContentSection — Section 2 of QuestionEditForm.
 *
 * Renders the question content block:
 *   - Question text (LaTeX editor)
 *   - Question diagram (admin only — needs onUploadImage)
 *   - Figures the text places by marker, on the stem and each part (FiguresEditor)
 *   - STRUCTURED parts editor (when questionType === 'STRUCTURED')
 *   - MCQ options editor (when questionType is MCQ / MCQ_MULTI) —
 *     delegated to `MCQOptionsEditor`, which owns its own state.
 *
 * State is owned by the parent (`QuestionEditForm`); this is presentational.
 * Extracted from `QuestionEditForm.tsx` during the 2026-05-17 split.
 */
import type { QuestionPart, PartType, PartRequirement } from '@padcanvas/shared';
import { LatexEditor } from '../../latex';
import { ImageUploadField } from '../ImageUploadField';
import type { QuestionFormData, UpdateField } from '../formTypes';
import { MCQOptionsEditor } from './MCQOptionsEditor';
import { PartRequirementEditor } from './PartRequirementEditor';
import { FiguresEditor } from './FiguresEditor';
import './QuestionContentSection.css';

/** Atomic types a part can take (Phase 1B). Order matches the question-level
 *  type dropdown for familiarity. */
const PART_TYPE_OPTIONS: Array<{ value: PartType; label: string }> = [
    { value: 'FREEFORM', label: 'Free form' },
    { value: 'MCQ', label: 'MCQ' },
    { value: 'MCQ_MULTI', label: 'MCQ - Multiple Correct' },
    { value: 'NUMERICAL', label: 'Numerical' },
    { value: 'SINGLELINE', label: 'Single Line' },
];

export interface QuestionContentSectionProps {
    form: QuestionFormData;
    updateField: UpdateField;
    onUploadImage?: (base64: string, mimeType: string) => Promise<{ url: string }>;
    /** STRUCTURED parts handlers — state is owned by the parent. */
    addPart: () => void;
    removePart: (index: number) => void;
    updatePart: <K extends keyof QuestionPart>(index: number, field: K, value: QuestionPart[K]) => void;
    /** STRUCTURED choice / K-of-N expression (Phase 1C). */
    onRequirementChange: (requirement: PartRequirement | undefined) => void;
}

export function QuestionContentSection({
    form,
    updateField,
    onUploadImage,
    addPart,
    removePart,
    updatePart,
    onRequirementChange,
}: QuestionContentSectionProps) {
    const isMCQ = form.questionType === 'MCQ' || form.questionType === 'MCQ_MULTI';
    const isStructured = form.questionType === 'STRUCTURED';

    return (
        <div className="form-section-group">
            <div className="form-section-group-header">Question</div>
            <div className="form-section-group-body">
                {/* Question Text */}
                <div className="form-section">
                    <label>Question Text</label>
                    <LatexEditor
                        value={form.questionText}
                        onChange={v => updateField('questionText', v)}
                        rows={4}
                        expandable
                    />
                    {onUploadImage && (
                        <FiguresEditor
                            text={form.questionText}
                            figures={form.figures}
                            onChange={(text, figures) => {
                                updateField('questionText', text);
                                updateField('figures', figures);
                            }}
                            onUploadImage={onUploadImage}
                            slotFor={figureId => ({ kind: 'questionFigure', figureId })}
                            takenIds={(form.parts ?? []).flatMap(p => (p.figures ?? []).map(f => f.id))}
                        />
                    )}
                </div>

                {/* Question Diagram (admin only — requires image upload) */}
                {onUploadImage && (
                    <ImageUploadField
                        label="Question Diagram URL"
                        value={form.questionDiagramUrl}
                        onChange={url => updateField('questionDiagramUrl', url)}
                        maxPreviewHeight={200}
                        onUploadImage={onUploadImage}
                        sourceSlot={{ kind: 'questionDiagram' }}
                    />
                )}

                {/* STRUCTURED Parts editor.
                    The parent Question Text above acts as the shared stem (common
                    context across parts). Each part has its own statement, type
                    (Phase 1B: FREEFORM / MCQ / MCQ_MULTI / NUMERICAL / SINGLELINE), diagram,
                    and a per-type answer editor.
                    See docs/FeatureDesigns/question-structure.md. */}
                {isStructured && (
                    <div className="form-section">
                        <div className="section-header">
                            <label>Parts</label>
                            <button type="button" className="btn-add-inline" onClick={addPart}>
                                + Add Part
                            </button>
                        </div>
                        <p className="empty-note" style={{ fontSize: '0.85rem', color: 'var(--text-tertiary)', marginBottom: '0.5rem' }}>
                            Use the <strong>Question Text</strong> above for the shared stem (common context, diagrams).
                            Each part below has its own statement, correct answer, and marks.
                        </p>
                        {(form.parts || []).length === 0 && (
                            <p className="empty-note">
                                No parts yet. Click <strong>+ Add Part</strong> to create the first lettered sub-part.
                            </p>
                        )}
                        {(form.parts || []).map((part, idx) => (
                            <div key={part.partId} className="part-editor-card">
                                <div className="part-editor-card-header">
                                    <strong className="part-editor-card-label">Part ({part.partId})</strong>
                                    <select
                                        className="part-editor-card-type"
                                        value={part.type}
                                        onChange={e => updatePart(idx, 'type', e.target.value as QuestionPart['type'])}
                                        title={`Answer type for part (${part.partId})`}
                                    >
                                        {PART_TYPE_OPTIONS.map(o => (
                                            <option key={o.value} value={o.value}>{o.label}</option>
                                        ))}
                                    </select>
                                    <label
                                        htmlFor={`part-${part.partId}-marks`}
                                        className="part-editor-card-marks"
                                    >
                                        Marks
                                        <input
                                            id={`part-${part.partId}-marks`}
                                            type="number"
                                            min="0"
                                            step="0.5"
                                            value={part.marks ?? ''}
                                            onChange={e => updatePart(idx, 'marks', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                                            placeholder="e.g. 4"
                                            title="Marks for this part"
                                        />
                                    </label>
                                    <button
                                        type="button"
                                        className="btn-remove-inline"
                                        onClick={() => removePart(idx)}
                                        title={`Remove part (${part.partId})`}
                                    >
                                        x
                                    </button>
                                </div>
                                <div className="part-editor-card-field">
                                    <label htmlFor={`part-${part.partId}-statement`}>Question Text</label>
                                    <LatexEditor
                                        value={part.statement ?? ''}
                                        onChange={v => updatePart(idx, 'statement', v)}
                                        rows={2}
                                        expandable
                                        placeholder={`Statement for part (${part.partId})…`}
                                    />
                                    {onUploadImage && (
                                        <FiguresEditor
                                            text={part.statement ?? ''}
                                            figures={part.figures}
                                            onChange={(text, figures) => {
                                                updatePart(idx, 'statement', text);
                                                updatePart(idx, 'figures', figures);
                                            }}
                                            onUploadImage={onUploadImage}
                                            slotFor={figureId => ({ kind: 'partFigure', partId: part.partId, figureId })}
                                            takenIds={[
                                                ...(form.figures ?? []).map(f => f.id),
                                                ...(form.parts ?? []).filter((_, i) => i !== idx).flatMap(p => (p.figures ?? []).map(f => f.id)),
                                            ]}
                                        />
                                    )}
                                </div>
                                {/* Optional per-part diagram (admin only — needs upload callback). */}
                                {onUploadImage && (
                                    <div className="part-editor-card-field">
                                        <ImageUploadField
                                            label="Part Diagram URL"
                                            value={part.diagramUrl ?? null}
                                            onChange={url => updatePart(idx, 'diagramUrl', url ?? undefined)}
                                            maxPreviewHeight={160}
                                            onUploadImage={onUploadImage}
                                            sourceSlot={{ kind: 'partDiagram', partId: part.partId }}
                                        />
                                    </div>
                                )}
                                {/* Per-type answer editors (Phase 1B). MCQ parts own their
                                    correctAnswer via the options editor (comma-joined correct
                                    labels); NUMERICAL takes a number + error margin; FREEFORM
                                    keeps the free-text expected answer. */}
                                {(part.type === 'MCQ' || part.type === 'MCQ_MULTI') && (
                                    <div className="part-editor-card-field">
                                        <MCQOptionsEditor
                                            options={part.options || []}
                                            isMulti={part.type === 'MCQ_MULTI'}
                                            onOptionsChange={opts => updatePart(idx, 'options', opts)}
                                            onCorrectAnswerChange={ans => updatePart(idx, 'correctAnswer', ans || undefined)}
                                            onUploadImage={onUploadImage}
                                            imageSlot={label => ({ kind: 'partOptionImage', partId: part.partId, option: label })}
                                        />
                                    </div>
                                )}
                                {part.type === 'NUMERICAL' && (
                                    <div className="part-editor-card-field part-editor-numerical-row">
                                        <label htmlFor={`part-${part.partId}-answer`}>
                                            Correct Answer
                                            <input
                                                id={`part-${part.partId}-answer`}
                                                type="text"
                                                inputMode="decimal"
                                                value={part.correctAnswer ?? ''}
                                                onChange={e => updatePart(idx, 'correctAnswer', e.target.value || undefined)}
                                                placeholder="e.g. 9.8"
                                            />
                                        </label>
                                        <label htmlFor={`part-${part.partId}-margin`}>
                                            Error Margin (±)
                                            <input
                                                id={`part-${part.partId}-margin`}
                                                type="number"
                                                min="0"
                                                step="any"
                                                value={part.errorMargin ?? ''}
                                                onChange={e => updatePart(idx, 'errorMargin', e.target.value === '' ? undefined : parseFloat(e.target.value))}
                                                placeholder="e.g. 0.1"
                                                title="Accepted tolerance around the correct value"
                                            />
                                        </label>
                                    </div>
                                )}
                                {part.type === 'SINGLELINE' && (
                                    <div className="part-editor-card-field">
                                        <label htmlFor={`part-${part.partId}-answer`}>Correct Answer</label>
                                        <input
                                            id={`part-${part.partId}-answer`}
                                            type="text"
                                            value={part.correctAnswer ?? ''}
                                            onChange={e => updatePart(idx, 'correctAnswer', e.target.value || undefined)}
                                            placeholder="e.g. Sodium  (text or number)"
                                        />
                                    </div>
                                )}
                                {part.type === 'FREEFORM' && (
                                    <div className="part-editor-card-field">
                                        <label htmlFor={`part-${part.partId}-answer`}>Correct Answer</label>
                                        <LatexEditor
                                            value={part.correctAnswer ?? ''}
                                            onChange={v => updatePart(idx, 'correctAnswer', v || undefined)}
                                            rows={2}
                                            expandable
                                            placeholder={`Expected answer for part (${part.partId})…`}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Phase 1C: how the parts combine (choice / K-of-N).
                            Renders only with ≥2 parts. */}
                        <PartRequirementEditor
                            parts={form.parts || []}
                            requirement={form.requirement}
                            onChange={onRequirementChange}
                        />
                    </div>
                )}

                {/* MCQ Options — delegated to a self-contained editor. */}
                {isMCQ && (
                    <MCQOptionsEditor
                        options={form.options || []}
                        isMulti={form.questionType === 'MCQ_MULTI'}
                        onOptionsChange={opts => updateField('options', opts)}
                        onCorrectAnswerChange={ans => updateField('correctAnswer', ans)}
                        onUploadImage={onUploadImage}
                        imageSlot={label => ({ kind: 'optionImage', option: label })}
                    />
                )}
            </div>
        </div>
    );
}
